import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CustomFieldType} from '@ee/common/enums';
import {UntypedFormGroup} from '@angular/forms';
import {Subscription} from 'rxjs';

@Component({
  selector: 'ee-custom-field',
  template: `
    <div class="w-full flex flex-row justify-center items-center" [formGroup]="form">
      @switch (form.get('type').value) {
        @case (fieldTypes.DATE) {
          <mat-form-field class="flex-1" appearance="outline"
                          [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput formControlName="value" [matDatepicker]="picker"
                   [required]="!builder && form.get('required').value">
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.DECIMAL) {
          <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <span matPrefix>$ &nbsp;</span>
            <input matInput type="number" formControlName="value"
                   [required]="!builder && form.get('required').value">
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.SELECT) {
          <mat-form-field class="flex-1" appearance="outline"
                          [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <mat-select formControlName="value" [required]="!builder && form.get('required').value">
              @for (option of form.get('options').value; track option) {
                <mat-option [value]="option">
                  {{option}}
                </mat-option>
              }
            </mat-select>
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.BOOLEAN) {
          <div class="flex-1">
            @if (form.get('true_label').value && form.get('false_label').value) {
              <mat-radio-group class="ee" formControlName="value">
                <mat-radio-button class="boolean-selection mr-4" [value]="form.get('true_label').value">
                  <b>{{form.get('true_label').value}}</b>
                </mat-radio-button>
                <mat-radio-button class="boolean-selection" [value]="form.get('false_label').value">
                  <b>{{form.get('false_label').value}}</b>
                </mat-radio-button>
              </mat-radio-group>
            } @else {
              <mat-checkbox formControlName="value" class="compact mb-4">
                {{form.get('label').value}}
              </mat-checkbox>
            }
          </div>
        }
        @case (fieldTypes.TIME) {
          <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput formControlName="value"
                   [required]="!builder && form.get('required').value" [ngxMatTimepicker]="matTimepicker">
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
            <ngx-mat-timepicker #matTimepicker [defaultTime]="'3:00 pm'"></ngx-mat-timepicker>
          </mat-form-field>
        }
        @case (fieldTypes.FILE_UPLOAD) {
          <ee-uploader class="w-full" [form]="$any(form.get('value'))"
                       [ngClass]="{'hidden-from-intake': hideFromIntake}"></ee-uploader>
        }
        @case (fieldTypes.CALCULATED) {
          <ng-container [ngTemplateOutlet]="computedPreview"></ng-container>
        }
        @case (fieldTypes.DATE_OFFSET) {
          <ng-container [ngTemplateOutlet]="computedPreview"></ng-container>
        }
        @default {
            <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput formControlName="value" [required]="!builder && form.get('required').value">
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
      }

      <ng-template #computedPreview>
        <mat-form-field class="flex-1" appearance="outline"
                        [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
          <mat-label>{{form.get('label').value}}</mat-label>
          <input matInput [placeholder]="computedPlaceholder" disabled readonly>
          <span matSuffix class="mr-2"><i [class]="computedIcon"></i></span>
          <mat-hint>Calculated automatically — the value appears on the case.</mat-hint>
        </mat-form-field>
      </ng-template>

      <ng-template #hint>
        <span>{{ form.get('required').value ? 'Required' : 'Optional' }}. </span>
        @if (form.get('hint')?.value) {
          <span>{{form.get('hint')?.value}}. </span>
        }
        @if (form.get('hint_more_info')?.value) {
          <span class="field-instructions-wrapper">
                  Hover for <span class="field-instructions" [tp]="form.get('hint_more_info').value" tpPlacement="bottom">
                  more info</span>.
          </span>
        }
      </ng-template>
    </div>
  `,
  styles: [`
    @use 'components/color-palette';

    .boolean-selection {
      min-width: 100px;
    }
    .field-instructions {
      color: var(--primary-red);
      text-decoration: underline;
      text-decoration-style: dashed;
      cursor: pointer;
      font-weight: 600;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomFieldComponent implements OnInit, OnDestroy {
  fieldTypes = CustomFieldType;
  @Input() form: UntypedFormGroup;
  @Input() builder = false;
  @Input() hideFromIntake = false;
  @Input() compact = true;

  private subs: Subscription[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.subs.push(this.form.valueChanges.subscribe( () => {
      this.cdr.detectChanges();
    }));
    this.subs.push(this.form.statusChanges.subscribe( () => {
      this.cdr.detectChanges();
    }));
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  /** Placeholder shown in the builder preview for computed (smart) fields, which have no entered value. */
  get computedPlaceholder(): string {
    return this.form.get('type')?.value === CustomFieldType.DATE_OFFSET
      ? 'Calculated date'
      : 'Calculated value';
  }

  get computedIcon(): string {
    return this.form.get('type')?.value === CustomFieldType.DATE_OFFSET
      ? 'far fa-calendar-days'
      : 'far fa-calculator';
  }
}
